import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/utils/format';
import { CodeStatusBadge } from './StatusBadge';
import type { AdminRequest } from '@/mocks/adminRequests';

type CodesTabProps = {
  requests: AdminRequest[];
  managerEmail: string;
  onRenew: (requestId: string, manager: string) => Promise<AdminRequest | null>;
  onInvalidate: (requestId: string, manager: string) => Promise<void>;
};

export default function CodesTab({ requests, managerEmail, onRenew, onInvalidate }: CodesTabProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return requests;
    return requests.filter(
      (request) =>
        request.reference.toLowerCase().includes(query) ||
        request.clientName.toLowerCase().includes(query) ||
        request.code.includes(query),
    );
  }, [requests, search]);

  const handleRenew = async (request: AdminRequest) => {
    const updated = await onRenew(request.id, managerEmail);
    if (updated) {
      setNotice(`${t('admin.codes.renewDone')} ${updated.code}`);
      setTimeout(() => setNotice(null), 4000);
    }
  };

  const handleInvalidate = async (request: AdminRequest) => {
    await onInvalidate(request.id, managerEmail);
    setNotice(t('admin.codes.invalidateDone'));
    setTimeout(() => setNotice(null), 4000);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-xl font-semibold text-foreground-950">
          {t('admin.codes.title')}
        </h1>
        <p className="mt-1 text-sm text-foreground-600">{t('admin.codes.subtitle')}</p>
      </div>

      <div className="relative">
        <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('admin.codes.searchPh')}
          className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
      </div>

      {notice && (
        <div className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-900">
          <i className="ri-checkbox-circle-line mt-0.5"></i>
          <span>{notice}</span>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-100 text-xs uppercase tracking-wide text-foreground-500">
                <th className="px-5 py-3 font-semibold">{t('admin.codes.reference')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.codes.client')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.codes.code')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.codes.status')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.codes.generatedAt')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.codes.expiresAt')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.codes.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-foreground-500">
                    {t('admin.codes.empty')}
                  </td>
                </tr>
              ) : (
                filtered.map((request) => (
                  <tr key={request.id} className="border-b border-background-100 last:border-0 hover:bg-background-50">
                    <td className="px-5 py-3 font-semibold text-foreground-800">{request.reference}</td>
                    <td className="px-5 py-3 text-foreground-700">{request.clientName}</td>
                    <td className="px-5 py-3">
                      <span className="font-heading text-base font-semibold tracking-[0.3em] text-foreground-950">
                        {request.code}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <CodeStatusBadge status={request.codeStatus} />
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-foreground-600">
                      {formatDateTime(request.codeGeneratedAt)}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-foreground-600">
                      {formatDateTime(request.codeExpiresAt)}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        {request.codeStatus !== 'used' && (
                          <button
                            type="button"
                            onClick={() => handleRenew(request)}
                            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                          >
                            <i className="ri-refresh-line"></i>
                            {t('admin.codes.renew')}
                          </button>
                        )}
                        {request.codeStatus === 'sent' && (
                          <button
                            type="button"
                            onClick={() => handleInvalidate(request)}
                            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 px-2.5 py-1.5 text-xs font-semibold text-accent-900 transition-colors hover:bg-accent-50"
                          >
                            <i className="ri-close-circle-line"></i>
                            {t('admin.codes.invalidate')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}